import styled from "@emotion/styled"
import { css } from "@emotion/react"

import NukaCarousel from "nuka-carousel"
import { GatsbyImage } from "gatsby-plugin-image"

import Section from "../../components/Section"
import BuyButton from "../../components/BuyButton"

import { mediaQueries } from "../../styles"
import NavButton from "../../components/ProductsCarousel/NavButton"

export const MainSection = styled(Section)`
  ${mediaQueries.max576} {
    padding-top: 1rem;
  }
`

export const StickyWrapper = styled.div`
  position: sticky;
  top: 106px;

  ${mediaQueries.max576} {
    position: relative;
    top: 0;
  }
`

export const Carousel = styled(NukaCarousel)`
  &:focus {
    outline: none;
  }

  ${mediaQueries.max576} {
    margin-bottom: 2rem;
  }
`

export const ProductImage = styled(GatsbyImage)`
  display: block;
  max-width: 100%;
  margin: 0 auto;
`

export const ProductName = styled.h2`
  margin-bottom: 0;
  color: var(--primary);
  font-size: 3rem;
  font-weight: 800;
  text-transform: uppercase;

  ${mediaQueries.max576} {
    font-size: 2rem;
  }
`

export const ProductWeight = styled.span`
  color: var(--secondary-light);
`

export const ProductContents = styled.span`
  margin-right: 2rem;
`

export const ProductPrice = styled.span`
  margin-bottom: 1.25rem;
  font-size: 3rem;
  color: var(--primary);
  font-weight: 300;

  ${mediaQueries.max576} {
    font-size: 1.5rem;
  }
`

export const ButtonGroup = styled.div`
  display: flex;
  margin-bottom: 2rem;

  ${mediaQueries.max576} {
    flex-direction: column;
  }
`

export const QtyButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.5rem;
  border: 1.5px solid var(--primary);
  line-height: 1;

  ${mediaQueries.max576} {
    margin-bottom: 1rem;
  }

  ${mediaQueries.min576} {
    margin-right: 0.5rem;
  }
`

export const Qty = styled.span`
  min-width: 30px;
  margin-left: 1.5rem;
  margin-right: 1.5rem;
  color: var(--primary);
  font-size: 1.5rem;
  text-align: center;
`

const qtyBaseButtons = css`
  width: 1.5rem;
  height: 1.5rem;
  appearance: none;
  padding: none;
  background: none;
  border: none;
  margin: none;
  color: var(--primary);
  font-size: 1.125rem;
  outline: none;

  &:focus {
    box-shadow: 0 0 0 1px var(--primary);
  }

  &:disabled {
    opacity: 0;
    pointer-events: none;
  }
`

export const Decrement = styled.button`
  ${qtyBaseButtons}
`

export const Increment = styled.button`
  ${qtyBaseButtons}
`

export const StyledBuyButton = styled(BuyButton)`
  padding-top: 1rem;
  padding-bottom: 1rem;
  border: 1.5px solid var(--primary);
`

export const Variants = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding-bottom: 1.5rem;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid var(--primary);
`

export const VariantLabel = styled.label`
  margin-right: 2rem;
  font-size: 0.75rem;
  font-weight: 300;
  cursor: pointer;
`

export const VariantInput = styled.input`
  @supports (-webkit-appearance: none) or (-moz-appearance: none) {
    --active: var(--primary);
    --active-inner: var(--merino);
    --focus: 2px rgba(39, 94, 254, 0.3);
    --border: var(--primary);
    --border-hover: var(--primary);
    --background: var(--merino);
    --disabled: #f6f8ff;
    --disabled-inner: #e1e6f9;
    -webkit-appearance: none;
    -moz-appearance: none;
    height: 10px;
    outline: none;
    display: inline-block;

    position: relative;
    margin: 0;
    cursor: pointer;
    border: 1px solid var(--bc, var(--border));
    background: var(--b, var(--background));
    margin-right: 0.5rem;
    transition: background 0.3s, border-color 0.3s, box-shadow 0.2s;
    &:after {
      content: "";
      display: block;
      left: 0;
      top: 0;
      position: absolute;
      transition: transform var(--d-t, 0.3s) var(--d-t-e, ease),
        opacity var(--d-o, 0.2s);
    }
    &:checked {
      --b: var(--active);
      --bc: var(--active);
      --d-o: 0.3s;
      --d-t: 0.6s;
      --d-t-e: cubic-bezier(0.2, 0.85, 0.32, 1.2);
    }
    &:disabled {
      --b: var(--disabled);
      cursor: not-allowed;
      opacity: 0.9;
      &:checked {
        --b: var(--disabled-inner);
        --bc: var(--border);
      }
      & + label {
        cursor: not-allowed;
      }
    }
    &:hover {
      &:not(:checked) {
        &:not(:disabled) {
          --bc: var(--border-hover);
        }
      }
    }
    &:focus {
      box-shadow: 0 0 0 var(--focus);
    }
    &:not(.switch) {
      width: 10px;
      &:checked {
        --o: 1;
      }
    }
    & + label {
      font-size: 14px;
      line-height: 10px;
      display: inline-block;
      vertical-align: top;
      cursor: pointer;
      margin-left: 4px;
    }

    border-radius: 50%;
    &:after {
      width: 19px;
      height: 19px;
      border-radius: 50%;
      background: var(--active-inner);
      opacity: 0;
      transform: scale(var(--s, 0.7));
    }
    &:checked {
      --s: 0.5;
    }
  }
`

export const ProductDetails = styled.div`
  font-size: 0.75rem;
  font-weight: 300;

  & > * {
    margin-bottom: 1em;
  }
`

export const RelatedProductsSection = styled(Section)`
  ${mediaQueries.max576} {
    padding-top: 0rem;

    h3 {
      font-size: 1.5rem;
    }
  }

  h3 {
    margin-left: auto;
    margin-right: auto;
    max-width: 24ch;
    text-align: center;
    text-transform: uppercase;
    letter-spacing: 2px;
  }
`

export const StyledNavButton = styled(NavButton)<{ placement: "left" | "right" }>`
  margin-left: ${({ placement }) => placement === "left" && `-1rem`};
  margin-right: ${({ placement }) => placement === "right" && `-1rem`};

  ${mediaQueries.min768} {
    margin-left: ${({ placement }) => placement === "left" && `1rem`};
    margin-right: ${({ placement }) => placement === "right" && `1rem`};
  }
`
